// ---------------------------------------------------------------------------
// App-embedded asset versioning.
//
// The APK ships the web assets inside itself (see scripts/build-native-assets.mjs).
// After the user installs an APK update, the files on disk are new — but the
// WebView may still be holding the PREVIOUS index.html (and its now-deleted
// hashed JS/CSS chunks) in its HTTP cache or in a service-worker cache. That
// mismatch is what produces "app updated but screen is blank / old UI".
//
// Fix: every native bundle is stamped with a build id.
//   * scripts/build-native-assets.mjs writes /bundle-version.json and injects
//     window.__BC_BUNDLE_ID into the shell HTML.
//   * At runtime we compare the id baked into the DOCUMENT we are running with
//     the id in the JSON file ON DISK (fetched with cache: "no-store").
//   * Different -> the document is stale. Purge caches, unregister service
//     workers, and reload once with a cache-busting query param.
//
// Web builds are unaffected: the check only runs from the local APK bundle.
// ---------------------------------------------------------------------------
import { isLocalBundle } from "./native-origin";

const VERSION_URL = "/bundle-version.json";
/** Guards against a reload loop if something goes wrong. */
const RELOAD_FLAG = "bc_bundle_reload";
/** Persists the last id we booted, purely for logging/diagnostics. */
const LAST_ID_KEY = "bc_bundle_last_id";
/** Survives reloads: how many purge+reload cycles we've burned on a build id. */
const ATTEMPTS_KEY = "bc_bundle_purge_attempts";
/** Set when we give up on automatic recovery and hand over to the user. */
const SAFE_MODE_KEY = "bc_bundle_safe_mode";

/** Hard ceiling on automatic purge + reload cycles for one build id. */
export const MAX_PURGE_ATTEMPTS = 3;
/** Attempts older than this are considered a fresh episode, not a loop. */
const ATTEMPT_WINDOW_MS = 10 * 60 * 1000;

export type BundleVersion = { buildId: string; builtAt?: string };

export type SafeModeState = { reason: string; buildId: string; attempts: number; at: string };

/** Fired when repeated cleanup attempts fail and safe mode takes over. */
export const BUNDLE_SAFE_MODE_EVENT = "bc:bundle-safe-mode";

type AttemptRecord = { buildId: string; count: number; firstAt: number };

function readAttempts(): AttemptRecord | null {
  try {
    const raw = window.localStorage.getItem(ATTEMPTS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AttemptRecord;
    if (typeof parsed?.buildId !== "string" || typeof parsed?.count !== "number") return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Increments (or starts) the attempt counter for a build id and returns it. */
function recordPurgeAttempt(buildId: string): number {
  const now = Date.now();
  const prev = readAttempts();
  const continuing = prev && prev.buildId === buildId && now - prev.firstAt < ATTEMPT_WINDOW_MS;
  const next: AttemptRecord = continuing
    ? { buildId, count: prev!.count + 1, firstAt: prev!.firstAt }
    : { buildId, count: 1, firstAt: now };
  try {
    window.localStorage.setItem(ATTEMPTS_KEY, JSON.stringify(next));
  } catch {
    /* noop */
  }
  return next.count;
}

/** Clears the attempt counter — call once the app boots on the expected build. */
export function resetPurgeAttempts(): void {
  try {
    window.localStorage.removeItem(ATTEMPTS_KEY);
  } catch {
    /* noop */
  }
}

/** Current safe-mode state, if the app gave up on automatic recovery. */
export function getSafeModeState(): SafeModeState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(SAFE_MODE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SafeModeState;
    return typeof parsed?.reason === "string" ? parsed : null;
  } catch {
    return null;
  }
}

export function isSafeMode(): boolean {
  return getSafeModeState() !== null;
}

function enterSafeMode(state: SafeModeState) {
  console.warn("[bundle] entering safe mode", state);
  try {
    window.localStorage.setItem(SAFE_MODE_KEY, JSON.stringify(state));
  } catch {
    /* noop */
  }
  try {
    window.dispatchEvent(new CustomEvent<SafeModeState>(BUNDLE_SAFE_MODE_EVENT, { detail: state }));
  } catch {
    /* noop */
  }
}

/**
 * Leaves safe mode. `retry` resets the attempt budget so the user can ask for
 * one more cleanup round; otherwise the app simply continues as-is.
 */
export function exitSafeMode(options?: { retry?: boolean }): void {
  try {
    window.localStorage.removeItem(SAFE_MODE_KEY);
  } catch {
    /* noop */
  }
  if (options?.retry) resetPurgeAttempts();
  try {
    window.sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* noop */
  }
}


/** Build id embedded in the currently running document, if any. */
export function getRunningBundleId(): string | null {
  if (typeof window === "undefined") return null;
  const id = (window as unknown as { __BC_BUNDLE_ID?: string }).__BC_BUNDLE_ID;
  return typeof id === "string" && id.length > 0 ? id : null;
}

/** Build id of the assets actually present on disk inside the APK. */
export async function fetchOnDiskBundleId(): Promise<string | null> {
  try {
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return null;
    const data = (await res.json()) as BundleVersion;
    return typeof data?.buildId === "string" && data.buildId ? data.buildId : null;
  } catch {
    return null;
  }
}

/**
 * IndexedDB databases that belong to the embedded bundle's asset/runtime layer
 * (service-worker precache metadata, build tooling, cached query snapshots).
 * Anything holding user/session data (Supabase auth, Firebase messaging) is
 * deliberately NOT matched — purging those would sign the user out.
 */
const BUNDLE_IDB_PATTERNS: RegExp[] = [
  /^workbox/i,
  /^workbox-expiration$/i,
  /^vite/i,
  /^bc-(cache|assets|bundle|query)/i,
  /-precache(-|$)/i,
  /^tanstack-query/i,
  /^keyval-store$/i,
];

const PRESERVED_IDB_PATTERNS: RegExp[] = [/supabase/i, /firebase/i, /auth/i, /fcm/i];

function isBundleIdb(name: string): boolean {
  if (PRESERVED_IDB_PATTERNS.some((re) => re.test(name))) return false;
  return BUNDLE_IDB_PATTERNS.some((re) => re.test(name));
}

type IdbDeleteOutcome = "deleted" | "blocked" | "error" | "timeout";

function deleteIdb(name: string): Promise<IdbDeleteOutcome> {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.deleteDatabase(name);
      // `blocked` fires when another tab/worker holds a connection: don't hang,
      // report it so the retry loop can try again after a backoff.
      req.onsuccess = () => resolve("deleted");
      req.onerror = () => resolve("error");
      req.onblocked = () => resolve("blocked");
      setTimeout(() => resolve("timeout"), 2000);
    } catch {
      resolve("error");
    }
  });
}

async function purgeBundleIndexedDb(): Promise<void> {
  if (typeof indexedDB === "undefined") return;
  const listable = indexedDB as IDBFactory & { databases?: () => Promise<{ name?: string }[]> };
  let names: string[] = [];
  if (typeof listable.databases === "function") {
    const dbs = await listable.databases();
    names = dbs.map((d) => d?.name).filter((n): n is string => typeof n === "string" && n.length > 0);
  }
  // Older WebViews don't implement indexedDB.databases(): fall back to the
  // known asset-layer database names we create ourselves.
  if (names.length === 0) names = ["workbox-expiration", "workbox-precache-v2", "bc-query-cache"];
  const targets = names.filter(isBundleIdb);
  if (targets.length === 0) return;
  console.warn("[bundle] purging IndexedDB", targets);
  const outcomes = await Promise.all(targets.map(async (n) => [n, await deleteIdb(n)] as const));
  const stuck = outcomes.filter(([, o]) => o !== "deleted");
  if (stuck.length > 0) {
    throw new Error(stuck.map(([n, o]) => `${n}: ${o}`).join("; "));
  }
}

export type PurgeStep = "serviceWorker" | "cacheStorage" | "indexedDB";

export type PurgeResult = {
  ok: boolean;
  failed: PurgeStep[];
  errors: Record<string, string>;
};

/** Event fired when the purge could not fully complete before reloading. */
export const BUNDLE_PURGE_FAILED_EVENT = "bc:bundle-purge-failed";

export type BundlePurgeFailedDetail = PurgeResult & { buildId: string; reloadUrl: string };

const PURGE_ATTEMPTS = 3;
const PURGE_BASE_DELAY_MS = 250;

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** Runs `fn` up to PURGE_ATTEMPTS times with exponential backoff (250/500/1000ms). */
async function withRetry(step: PurgeStep, fn: () => Promise<void>): Promise<string | null> {
  let lastError = "unknown error";
  for (let attempt = 0; attempt < PURGE_ATTEMPTS; attempt++) {
    try {
      await fn();
      if (attempt > 0) console.warn(`[bundle] ${step} purge succeeded on retry ${attempt}`);
      return null;
    } catch (err) {
      lastError = err instanceof Error ? err.message : String(err);
      console.warn(`[bundle] ${step} purge attempt ${attempt + 1} failed: ${lastError}`);
      if (attempt < PURGE_ATTEMPTS - 1) await sleep(PURGE_BASE_DELAY_MS * 2 ** attempt);
    }
  }
  return lastError;
}

async function unregisterServiceWorkers(): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  const regs = await navigator.serviceWorker.getRegistrations();
  const results = await Promise.all(
    regs.map(async (r) => {
      try {
        r.waiting?.postMessage({ type: "SKIP_WAITING" });
      } catch {
        /* noop */
      }
      return r.unregister();
    }),
  );
  // A `false` result means the registration is still installed: surface it so
  // the retry loop gets another shot at it.
  if (results.some((ok) => ok === false)) throw new Error("a service worker refused to unregister");
}

async function clearCacheStorage(): Promise<void> {
  if (!("caches" in window)) return;
  const keys = await caches.keys();
  if (keys.length > 0) console.warn("[bundle] purging Cache Storage", keys);
  const results = await Promise.all(keys.map((k) => caches.delete(k)));
  if (results.some((ok) => ok === false)) throw new Error("a cache bucket could not be deleted");
  const remaining = await caches.keys();
  if (remaining.length > 0) throw new Error(`cache buckets remain: ${remaining.join(", ")}`);
}

async function purgeCaches(): Promise<PurgeResult> {
  const failed: PurgeStep[] = [];
  const errors: Record<string, string> = {};

  const record = (step: PurgeStep, error: string | null) => {
    if (error) {
      failed.push(step);
      errors[step] = error;
    }
  };

  // Each step runs independently: one failure must not skip the others.
  // 1. Unregister service workers FIRST so nothing re-populates the caches.
  record("serviceWorker", await withRetry("serviceWorker", unregisterServiceWorkers));
  // 2. Delete every Cache Storage bucket.
  record("cacheStorage", await withRetry("cacheStorage", clearCacheStorage));
  // 3. Drop IndexedDB stores tied to the bundle (auth/session data preserved).
  record("indexedDB", await withRetry("indexedDB", purgeBundleIndexedDb));

  return { ok: failed.length === 0, failed, errors };
}

/**
 * Compares the running document's build id with the one on disk and, when they
 * differ, hard-reloads onto the freshly installed bundle. Safe to call more
 * than once (startup + app resume); it reloads at most once per session.
 */
export async function checkEmbeddedBundleVersion(): Promise<void> {
  if (typeof window === "undefined") return;
  if (!isLocalBundle()) return;
  // Safe mode means automatic recovery already failed repeatedly: never loop.
  if (isSafeMode()) return;

  const running = getRunningBundleId();
  const onDisk = await fetchOnDiskBundleId();
  if (!onDisk) return;

  try {
    window.sessionStorage.setItem(LAST_ID_KEY, onDisk);
  } catch {
    /* noop */
  }

  if (running && running === onDisk) {
    // Booted on the expected build: the recovery episode is over.
    resetPurgeAttempts();
    return;
  }

  let alreadyReloaded = false;
  try {
    alreadyReloaded = window.sessionStorage.getItem(RELOAD_FLAG) === onDisk;
  } catch {
    /* noop */
  }

  const url = new URL(window.location.href);
  url.searchParams.set("_bc", onDisk);
  const reloadUrl = url.toString();

  const priorAttempts = readAttempts();
  const usedAttempts = priorAttempts?.buildId === onDisk ? priorAttempts.count : 0;
  if (usedAttempts >= MAX_PURGE_ATTEMPTS) {
    enterSafeMode({
      reason: `Automatic cleanup failed ${usedAttempts} times in a row.`,
      buildId: onDisk,
      attempts: usedAttempts,
      at: new Date().toISOString(),
    });
    return;
  }

  if (alreadyReloaded) {
    console.warn("[bundle] version mismatch persisted after reload; continuing", { running, onDisk });
    // The automatic path already had its turn — hand control to the user.
    emitPurgeFailure({
      ok: false,
      failed: [],
      errors: { reload: "stale bundle still active after an automatic reload" },
      buildId: onDisk,
      reloadUrl,
    });
    return;
  }

  console.warn("[bundle] embedded assets updated — reloading", { running, onDisk });
  try {
    window.sessionStorage.setItem(RELOAD_FLAG, onDisk);
  } catch {
    /* noop */
  }

  const attempt = recordPurgeAttempt(onDisk);
  const result = await purgeCaches();
  if (!result.ok) {
    if (attempt >= MAX_PURGE_ATTEMPTS) {
      // Budget exhausted: stop reloading and show the safe-mode view instead.
      enterSafeMode({
        reason: `Cleanup could not finish after ${attempt} attempts (${result.failed.join(", ")}).`,
        buildId: onDisk,
        attempts: attempt,
        at: new Date().toISOString(),
      });
      return;
    }
    // Reload anyway: a partial purge usually still lands on the new bundle.
    // The event lets the UI offer a manual retry if it doesn't.
    console.warn("[bundle] purge incomplete, reloading anyway", result);
    emitPurgeFailure({ ...result, buildId: onDisk, reloadUrl });
  }
  window.location.replace(reloadUrl);
}

function emitPurgeFailure(detail: BundlePurgeFailedDetail) {
  try {
    window.dispatchEvent(new CustomEvent<BundlePurgeFailedDetail>(BUNDLE_PURGE_FAILED_EVENT, { detail }));
  } catch {
    /* noop */
  }
}

/**
 * User-initiated recovery: retry the full purge (with backoff) and hard-reload.
 * Counts against the same attempt budget; once it's exhausted the app switches
 * to safe mode instead of reloading again.
 */
export async function forceBundleReload(): Promise<void> {
  if (typeof window === "undefined") return;
  const onDisk = (await fetchOnDiskBundleId()) ?? getRunningBundleId() ?? String(Date.now());
  try {
    window.sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* noop */
  }
  const attempt = recordPurgeAttempt(onDisk);
  const result = await purgeCaches();
  if (!result.ok) {
    console.warn("[bundle] manual purge still incomplete", result);
    if (attempt >= MAX_PURGE_ATTEMPTS) {
      enterSafeMode({
        reason: `Cleanup could not finish after ${attempt} attempts (${result.failed.join(", ")}).`,
        buildId: onDisk,
        attempts: attempt,
        at: new Date().toISOString(),
      });
      return;
    }
  }
  const url = new URL(window.location.href);
  url.searchParams.set("_bc", `${onDisk}-${Date.now()}`);
  window.location.replace(url.toString());
}


let installed = false;

/**
 * Runs the check at startup and again whenever the app returns to the
 * foreground (an APK update can be installed while the app is backgrounded).
 */
export function installBundleVersionWatcher(): void {
  if (installed) return;
  if (typeof window === "undefined") return;
  if (!isLocalBundle()) return;
  installed = true;

  void checkEmbeddedBundleVersion();

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void checkEmbeddedBundleVersion();
  });

  void (async () => {
    try {
      const { App } = await import("@capacitor/app");
      await App.addListener("resume", () => {
        void checkEmbeddedBundleVersion();
      });
    } catch {
      /* plugin unavailable — visibilitychange still covers it */
    }
  })();
}
